/**
 * Button — Modernize-style action primitive.
 *
 * Phase 12 refresh: flat, solid Modernize buttons with soft shadow
 * + hover elevation. Gradients are out; solid brand-600 with
 * shadow-brand-600/25 is in.
 *
 * Variants:
 *   - default: solid brand-600, white text, hover brand-700
 *   - secondary: slate-100 bg, slate text, hover slate-200
 *   - outline: white bg + slate border, hover slate-50
 *   - ghost: transparent, hover slate-100
 *   - destructive: solid rose-600 with matching shadow
 *
 * Sizes stay the same; added a subtle press translate so buttons
 * feel tactile without the old scale-97 squeeze.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold " +
    "transition-all duration-150 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 focus-visible:ring-offset-2 " +
    "disabled:pointer-events-none disabled:opacity-50 " +
    "active:translate-y-[1px] " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-brand-600 text-white shadow-sm shadow-brand-600/25 hover:bg-brand-700 hover:shadow-md hover:shadow-brand-600/30",
        secondary:
          "bg-slate-100 text-slate-900 hover:bg-slate-200 " +
          "dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700",
        outline:
          "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:border-slate-300 " +
          "dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
        ghost:
          "bg-transparent text-slate-700 hover:bg-slate-100 " +
          "dark:text-slate-200 dark:hover:bg-slate-800",
        destructive:
          "bg-rose-600 text-white shadow-sm shadow-rose-600/25 hover:bg-rose-700 hover:shadow-md hover:shadow-rose-600/30",
      },
      size: {
        sm: "h-8 px-3 text-xs rounded-lg",
        default: "h-10 px-4 py-2",
        lg: "h-11 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
